import React, { useEffect, useRef, useState } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { HTTP_STATUS } from '../../utils/httpStatusCodes';

const GOOGLE_MAPS_API_KEY = 'AIzaSyCb6XBv9tnLgqhPfWHBlzokjXagwfX3vtk';

const initialState = {
  first_name: '',
  last_name: '',
  mail: '',
  number: '',
  mobile: '',
  name: '',
  subscribed: false,
  address_line_1: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  latitude: '',
  longitude: '',
};

const AddCustomerModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const addressRef = useRef(null);

  
  const loadGoogleScript = () =>
    new Promise((resolve) => {
      if (window.google?.maps?.places) return resolve();
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = resolve;
      document.body.appendChild(script);
    });

  useEffect(() => {
    if (!isOpen) return;

    loadGoogleScript().then(() => {
      if (!addressRef.current) return;

      const autocomplete = new window.google.maps.places.Autocomplete(addressRef.current, {
        componentRestrictions: { country: 'au' },
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        setFormData((prev) => {
          const updated = { ...prev };
          updated.address_line_1 = place.formatted_address || '';
          updated.latitude = place.geometry?.location?.lat();
          updated.longitude = place.geometry?.location?.lng();

          place.address_components?.forEach((c) => {
            if (c.types.includes('locality')) updated.city = c.long_name;
            if (c.types.includes('administrative_area_level_1')) updated.state = c.long_name;
            if (c.types.includes('country')) updated.country = c.long_name;
            if (c.types.includes('postal_code')) updated.postal_code = c.long_name;
          });

          return updated;
        });
      });
    });
  
  }, [isOpen]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `.pac-container { z-index: 8000 !important; }`;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    setErrors((p) => ({ ...p, [name]: '' })); // clear error on change
  };

  const validate = () => {
    const temp = {};
    if (!formData.first_name) temp.first_name = 'First name is required';
    if (!formData.number) temp.number = 'Phone number is required';
    if (formData.mail && !/\S+@\S+\.\S+/.test(formData.mail)) temp.mail = 'Invalid email format';
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
  
    setLoading(true);
    setErrors({});
    setSuccessMsg('');
  
    try {
      const res = await callAPI(apiUrls.SAVE_CUSTOMER, {}, 'POST', {
        ...formData,
        name: `${formData.first_name} ${formData.last_name}`,
        mobile: formData.number,
        mail: formData.mail,
      });
  
      if (res.status === HTTP_STATUS.CREATED || res?.data?.status === 1) {
        setSuccessMsg('Customer added successfully');
        setFormData(initialState);
  
        setTimeout(() => {
          onSuccess();
        }, 600);
      } else if (res.data?.errors) {
  
        const backendToFormMap = {
          mobile: 'number',
          mail: 'mail',
          first_name: 'first_name',
          last_name: 'last_name',
          name: 'name',
          address_line_1: 'address_line_1',
          city: 'city',
          state: 'state',
          country: 'country',
          postal_code: 'postal_code',
        };
  
        const formattedErrors = {};
        Object.keys(res.data.errors).forEach((key) => {
          const field = backendToFormMap[key] || key;
          formattedErrors[field] = Array.isArray(res.data.errors[key])
            ? res.data.errors[key][0]
            : res.data.errors[key];
        });
  
        setErrors(formattedErrors);
  
  
        if (res.data.message) {
          setErrors((prev) => ({ ...prev, general: res.data.message }));
        }
      } else if (res.data?.message) {
  
        setErrors({ general: res.data.message });
      } else {
        setErrors({ general: 'Failed to add customer' });
      }
    } catch {
      setErrors({ general: 'Server error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  
  
  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="addCustomerModal">
      <div className="modal modalStyle show d-block">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title mdTitle">Add a New Customer</h1>
              <button className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body">
              <div className="modalBodyStyle formStyle">
                {errors.general && <div className="alert alert-danger">{errors.general}</div>}
                {successMsg && <div className="alert alert-success">{successMsg}</div>}

                <div className="mb-3">
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                    placeholder="First Name"
                  />
                  <div className="invalid-feedback">{errors.first_name || ' '}</div>
                </div>

                <div className="mb-3">
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                    placeholder="Last Name"
                  />
                  <div className="invalid-feedback">{errors.last_name || ' '}</div>
                </div>

                <div className="mb-3">
                  <input
                    type="email"
                    name="mail"
                    value={formData.mail}
                    onChange={handleChange}
                    className={`form-control ${errors.mail ? 'is-invalid' : ''}`}
                    placeholder="Email Address"
                  />
                  <div className="invalid-feedback">{errors.mail || ' '}</div>
                </div>

                <div className="mb-3">
                  <input
                    type="tel"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    className={`form-control ${errors.number ? 'is-invalid' : ''}`}
                    placeholder="Phone Number"
                  />
                  <div className="invalid-feedback">{errors.number || ' '}</div>
                </div>

                <div className="mb-3">
                  <input
                    ref={addressRef}
                    type="text"
                    name="address_line_1"
                    value={formData.address_line_1}
                    onChange={handleChange}
                    className={`form-control ${errors.address_line_1 ? 'is-invalid' : ''}`}
                    placeholder="Start typing your address and select from suggestions"
                  />
                  <div className="invalid-feedback">{errors.address_line_1 || ' '}</div>
                </div>

                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    name="subscribed"
                    checked={formData.subscribed}
                    onChange={handleChange}
                    className="form-check-input"
                    id="vouchers"
                  />
                  <label className="form-check-label" htmlFor="vouchers">
                    Receive discount vouchers & special promotions.
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer justify-content-between">
              <button className="btn btnLight" onClick={onClose}>
                Close
              </button>
              <button className="btn btnPrimary" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default AddCustomerModal;
